import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Folder, TrendingUp, CheckCircle2 } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Projects = () => {
  const { projects } = portfolioData;

  return (
    <section id="projects" className="py-20 bg-slate-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-10 w-72 h-72 bg-blue-600/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Featured <span className="text-blue-400">Projects</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Key projects demonstrating expertise in QA automation, system design, and engineering leadership
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="bg-slate-900/50 border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02] p-6 group"
              >
                {/* Project Header */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors">
                        <Folder className="text-blue-400" size={24} />
                      </div>
                      <Badge className="bg-slate-800 border-slate-600 text-gray-300 text-xs">
                        {project.category}
                      </Badge>
                    </div>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {project.name}
                  </h3>

                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                    {project.description}
                  </p>
                </div>

                {/* Achievements */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Key Achievements
                  </h4>
                  <ul className="space-y-2">
                    {project.achievements.slice(0, 3).map((achievement, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-400 text-xs sm:text-sm">
                        <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5"></div>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Impact */}
                <div className="mb-4 p-3 bg-blue-600/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="text-blue-400" size={16} />
                    <span className="text-xs font-semibold text-blue-400">Impact</span>
                  </div>
                  <p className="text-sm text-gray-300">{project.impact}</p>
                </div>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="border-slate-600 text-gray-400 text-xs hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;